/**
 * Pure H OS · Enterprise Document Engine · Frontend actions strip.
 *
 * A single reusable component that surfaces Preview / PDF / Excel / CSV /
 * Print actions for any of the 34+ documents registered in the backend
 * `pureh/documents.py` registry.
 *
 * Renders as a horizontal chip row using the existing Button primitive —
 * no new visual language.
 */
import { useState } from "react";
import { Button } from "./lib";

declare const process: { env: Record<string, string | undefined> };

// Same origin as `services/api.ts`. Direct `fetch` because the endpoints
// return binary streams (PDF / XLSX / CSV), not JSON.
const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

type PageSize = "A4" | "Letter";
type Orientation = "portrait" | "landscape";

export function DocumentActions({
  docKey,
  recordId,
  label,
  pageSize = "A4",
  orientation = "portrait",
  testId,
  compact = false,
}: {
  docKey: string;
  recordId?: string;
  label?: string;
  pageSize?: PageSize;
  orientation?: Orientation;
  testId?: string;
  compact?: boolean;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailNote, setEmailNote] = useState("");
  const [emailOk, setEmailOk] = useState<string | null>(null);

  // When `recordId` is provided we render the single-record variants of
  // every action. Aggregate mode (no recordId) hits the old endpoints and
  // still exports "list of many records" documents.
  const singleMode = Boolean(recordId);
  const routeBase  = singleMode
    ? `${BASE}/documents/single/${docKey}/${recordId}`
    : `${BASE}/documents/${docKey}`;
  const fileBase   = singleMode ? `${docKey}_${recordId}` : docKey;

  async function download(kind: "pdf" | "xlsx" | "csv", ext: string, mime: string) {
    setBusy(kind); setErr(null);
    try {
      // Single-record mode only exposes PDF today (XLSX/CSV for one row
      // is degenerate). Aggregates keep all three.
      if (singleMode && kind !== "pdf") {
        throw new Error("Only PDF is available for single-record export");
      }
      const qs = kind === "pdf" ? `?page_size=${pageSize}&orientation=${orientation}` : "";
      const res = await fetch(`${routeBase}/${kind}${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(new Blob([blob], { type: mime }));
      const a = document.createElement("a");
      a.href = url; a.download = `${fileBase}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Download failed");
    } finally { setBusy(null); }
  }

  async function preview() {
    setBusy("preview"); setErr(null);
    try {
      const res = await fetch(`${routeBase}/preview`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { html } = await res.json();
      const w = window.open("", "_blank");
      if (w) {
        w.document.open();
        w.document.write(html);
        w.document.close();
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Preview failed");
    } finally { setBusy(null); }
  }

  async function printDoc() {
    setBusy("print"); setErr(null);
    try {
      const res = await fetch(`${routeBase}/preview`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { html } = await res.json();
      const w = window.open("", "_blank");
      if (w) {
        w.document.open();
        w.document.write(html);
        w.document.close();
        setTimeout(() => { try { w.focus(); w.print(); } catch { /* popup blocked */ } }, 500);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Print failed");
    } finally { setBusy(null); }
  }

  async function sendEmail() {
    if (!emailTo.trim()) { setErr("Recipient email required"); return; }
    setBusy("email"); setErr(null); setEmailOk(null);
    try {
      const res = await fetch(`${BASE}/email/send-document`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ docKey, recordId, to: emailTo.trim(), message: emailNote.trim() || undefined }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      setEmailOk(`Sent to ${emailTo.trim()} ✓`);
      setEmailOpen(false); setEmailTo(""); setEmailNote("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Email failed");
    } finally { setBusy(null); }
  }

  return (
    <div className="flex items-center gap-2" data-testid={testId ?? `doc-actions-${docKey}${recordId ? "-" + recordId : ""}`}>
      {label && !compact && (
        <span className="text-[10px] uppercase tracking-[0.16em] mr-1" style={{ color: "var(--ph-muted)" }}>{label}</span>
      )}
      <Button variant="ghost" onClick={preview}                   disabled={busy !== null} testId={`doc-preview-${docKey}${recordId ? "-" + recordId : ""}`}>
        {busy === "preview" ? "…" : "Preview"}
      </Button>
      <Button variant="ghost" onClick={() => download("pdf",  "pdf",  "application/pdf")} disabled={busy !== null} testId={`doc-pdf-${docKey}${recordId ? "-" + recordId : ""}`}>
        {busy === "pdf"   ? "…" : "PDF"}
      </Button>
      {!singleMode && (
        <>
          <Button variant="ghost" onClick={() => download("xlsx", "xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")} disabled={busy !== null} testId={`doc-xlsx-${docKey}`}>
            {busy === "xlsx"  ? "…" : "Excel"}
          </Button>
          <Button variant="ghost" onClick={() => download("csv",  "csv",  "text/csv")} disabled={busy !== null} testId={`doc-csv-${docKey}`}>
            {busy === "csv"   ? "…" : "CSV"}
          </Button>
        </>
      )}
      <Button variant="ghost" onClick={printDoc}                  disabled={busy !== null} testId={`doc-print-${docKey}${recordId ? "-" + recordId : ""}`}>
        {busy === "print" ? "…" : "Print"}
      </Button>
      <Button variant="ghost" onClick={() => { setEmailOpen((o) => !o); setEmailOk(null); }} disabled={busy !== null} testId={`doc-email-${docKey}${recordId ? "-" + recordId : ""}`}>
        {busy === "email" ? "…" : "Email"}
      </Button>
      {emailOpen && (
        <span className="inline-flex items-center gap-1" data-testid={`doc-email-form-${docKey}${recordId ? "-" + recordId : ""}`}>
          <input
            value={emailTo}
            onChange={(e) => setEmailTo(e.target.value)}
            placeholder="recipient@email.com"
            data-testid={`doc-email-to-${docKey}${recordId ? "-" + recordId : ""}`}
            className="text-xs px-2 py-1 rounded border"
            style={{ borderColor: "var(--ph-border-strong)", width: 180 }}
          />
          <input
            value={emailNote}
            onChange={(e) => setEmailNote(e.target.value)}
            placeholder="note (optional)"
            data-testid={`doc-email-note-${docKey}${recordId ? "-" + recordId : ""}`}
            className="text-xs px-2 py-1 rounded border"
            style={{ borderColor: "var(--ph-border-strong)", width: 140 }}
          />
          <Button variant="ghost" onClick={sendEmail} disabled={busy !== null} testId={`doc-email-send-${docKey}${recordId ? "-" + recordId : ""}`}>Send</Button>
        </span>
      )}
      {emailOk && <span className="text-xs ml-2" style={{ color: "var(--ph-success)" }} data-testid={`doc-email-ok-${docKey}${recordId ? "-" + recordId : ""}`}>{emailOk}</span>}
      {err && <span className="text-xs ml-2" style={{ color: "var(--ph-danger)" }} data-testid={`doc-err-${docKey}${recordId ? "-" + recordId : ""}`}>{err}</span>}
    </div>
  );
}
